import { useState, useRef, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { Portal } from '../../components/Portal'
import { reverseGeocode, geocodeCity } from '../../lib/weather'

const EDGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`

const PANEL_LABELS = {
  schedule_today:      'Today\'s Schedule',
  upcoming_events:     'Upcoming Events',
  chores_tasks:        'Chores & Tasks',
  meal_plan:           'Meal Plan',
  announcements:       'Announcements',
  custody_transitions: 'Custody Schedule',
  weather:             'Weather',
  upcoming_bills:      'Upcoming Bills',
  birthdays:           'Birthdays',
  family_feed:         'Family Feed',
}

const DEFAULT_PANELS = [
  { id: 'schedule_today',      enabled: true,  duration_seconds: 30, admin_only: false },
  { id: 'upcoming_events',     enabled: true,  duration_seconds: 30, admin_only: false },
  { id: 'chores_tasks',        enabled: true,  duration_seconds: 30, admin_only: false },
  { id: 'meal_plan',           enabled: true,  duration_seconds: 30, admin_only: false },
  { id: 'announcements',       enabled: true,  duration_seconds: 20, admin_only: false },
  { id: 'custody_transitions', enabled: true,  duration_seconds: 20, admin_only: false },
  { id: 'weather',             enabled: true,  duration_seconds: 30, admin_only: false },
  { id: 'upcoming_bills',      enabled: true,  duration_seconds: 20, admin_only: true  },
  { id: 'birthdays',           enabled: true,  duration_seconds: 20, admin_only: false },
  { id: 'family_feed',         enabled: true,  duration_seconds: 30, admin_only: false },
]

const TIMEOUT_OPTIONS = [
  { label: '30 seconds',  value: 30 },
  { label: '1 minute',    value: 60 },
  { label: '2 minutes',   value: 120 },
  { label: '5 minutes',   value: 300 },
  { label: '10 minutes',  value: 600 },
]

const EMOJI_OPTIONS = ['😀','🙂','😎','🥳','🤩','👦','👧','👨','👩','👴','👵','🐶','🐱','🦊','🐼','🦄','⭐','🌟','🎸','⚽']

export function DisplaySettingsPage() {
  const session      = useAuthStore((s) => s.session)
  const householdId  = useAuthStore((s) => s.household?.id) ?? null

  const [devices,    setDevices]    = useState([])
  const [settings,   setSettings]   = useState(null)
  const [members,    setMembers]    = useState([])
  const [memberDSettings, setMDSettings] = useState({})
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState(false)

  // Register device flow
  const [showRegister,  setShowRegister]  = useState(false)
  const [newDeviceName, setNewDeviceName] = useState('')
  const [qrCode,        setQrCode]        = useState(null) // short code e.g. "AB3K9Z"
  const [copied,        setCopied]        = useState(false)
  const [registering,   setRegistering]   = useState(false)

  // PIN modal
  const [pinModal, setPinModal] = useState(null) // { userId, displayName }
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState('')

  // Location search
  const [locationSearch,   setLocationSearch]   = useState('')
  const [locationSearching, setLocationSearching] = useState(false)
  const [locationError,    setLocationError]    = useState('')

  // DnD
  const dragIdx = useRef(null)

  // ── Load data ──────────────────────────────────────────────
  useEffect(() => {
    if (!householdId) return
    loadAll()
  }, [householdId])

  async function loadAll() {
    setLoading(true)
    try {
      const [devRes, setRes, memRes, mdsRes] = await Promise.all([
        supabase.from('display_devices').select('*').eq('household_id', householdId).order('created_at'),
        supabase.from('display_settings').select('*').eq('household_id', householdId).maybeSingle(),
        supabase.from('household_members')
          .select('user_id, role, profiles(id, display_name, avatar_url, member_color)')
          .eq('household_id', householdId),
        supabase.from('member_display_settings').select('*').eq('household_id', householdId),
      ])

      setDevices(devRes.data ?? [])
      // Merge any new DEFAULT_PANELS that aren't in the saved config yet
      const savedData = setRes.data
      if (savedData?.panel_config) {
        const existingIds = new Set(savedData.panel_config.map((p) => p.id))
        const missing = DEFAULT_PANELS.filter((p) => !existingIds.has(p.id))
        if (missing.length > 0) savedData.panel_config = [...savedData.panel_config, ...missing]
      }
      setSettings(savedData ?? {
        idle_timeout_seconds: 120,
        panel_config: DEFAULT_PANELS,
        show_weather: false,
        weather_latitude: null,
        weather_longitude: null,
        weather_unit: 'fahrenheit',
        weather_location_label: null,
      })
      setMembers(memRes.data ?? [])

      const mdsMap = {}
      for (const m of mdsRes.data ?? []) {
        mdsMap[m.user_id] = m
      }
      setMDSettings(mdsMap)
    } finally {
      setLoading(false)
    }
  }

  // ── Save settings ──────────────────────────────────────────
  async function saveSettings() {
    setSaving(true)
    try {
      await supabase.from('display_settings').upsert(
        { ...settings, household_id: householdId },
        { onConflict: 'household_id' },
      )
    } finally {
      setSaving(false)
    }
  }

  // ── Register device ────────────────────────────────────────
  async function registerDevice() {
    if (!newDeviceName.trim()) return
    setRegistering(true)
    try {
      const res = await fetch(`${EDGE_BASE}/register-display`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ household_id: householdId, device_name: newDeviceName.trim() }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setQrCode(json.short_code)
      await loadAll()
    } catch (e) {
      alert(e.message)
    } finally {
      setRegistering(false)
    }
  }

  async function removeDevice(id) {
    if (!confirm('Remove this display device?')) return
    await supabase.from('display_devices').delete().eq('id', id)
    setDevices((d) => d.filter((x) => x.id !== id))
  }

  // ── Panel drag-and-drop ────────────────────────────────────
  function handleDragStart(i) { dragIdx.current = i }
  function handleDragOver(e)  { e.preventDefault() }
  function handleDrop(i) {
    const from = dragIdx.current
    if (from == null || from === i) return
    setSettings((s) => {
      const panels = [...(s.panel_config ?? DEFAULT_PANELS)]
      const [moved] = panels.splice(from, 1)
      panels.splice(i, 0, moved)
      return { ...s, panel_config: panels }
    })
    dragIdx.current = null
  }

  function updatePanel(id, updates) {
    setSettings((s) => ({
      ...s,
      panel_config: (s.panel_config ?? DEFAULT_PANELS).map((p) =>
        p.id === id ? { ...p, ...updates } : p
      ),
    }))
  }

  // ── Member display settings ────────────────────────────────
  async function saveMemberSetting(userId, updates) {
    setMDSettings((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] ?? {}), user_id: userId, household_id: householdId, ...updates },
    }))
    await supabase.from('member_display_settings').upsert(
      { user_id: userId, household_id: householdId, ...updates },
      { onConflict: 'user_id,household_id' },
    )
  }

  // ── PIN management ─────────────────────────────────────────
  async function savePin() {
    if (pinInput && !/^\d{4}$/.test(pinInput)) {
      setPinError('PIN must be exactly 4 digits')
      return
    }
    setSaving(true)
    setPinError('')
    try {
      const res = await fetch(`${EDGE_BASE}/set-display-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          target_user_id: pinModal.userId,
          household_id: householdId,
          pin: pinInput || null,
        }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setPinModal(null)
      setPinInput('')
    } catch (e) {
      setPinError(e.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Location search / detect ───────────────────────────────
  async function searchLocation() {
    if (!locationSearch.trim()) return
    setLocationSearching(true)
    setLocationError('')
    try {
      const { lat, lon, label } = await geocodeCity(locationSearch.trim())
      setSettings((s) => ({ ...s, weather_latitude: lat, weather_longitude: lon, weather_location_label: label }))
      setLocationSearch('')
    } catch (e) {
      setLocationError(e.message ?? 'City not found')
    } finally {
      setLocationSearching(false)
    }
  }

  function detectLocation() {
    navigator.geolocation?.getCurrentPosition(async (pos) => {
      const lat = pos.coords.latitude
      const lon = pos.coords.longitude
      try {
        const label = await reverseGeocode(lat, lon)
        setSettings((s) => ({ ...s, weather_latitude: lat, weather_longitude: lon, weather_location_label: label }))
      } catch {
        setSettings((s) => ({ ...s, weather_latitude: lat, weather_longitude: lon }))
      }
    }, () => alert('Location access denied'))
  }

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return <div className="p-6 text-text-secondary">Loading display settings…</div>
  }

  const panels = settings?.panel_config ?? DEFAULT_PANELS
  const deviceUrl = qrCode ? `https://doorchart.app/d/${qrCode}` : ''
  const shortUrl  = qrCode ? `doorchart.app/d/${qrCode}` : ''

  const copyUrl = () => {
    navigator.clipboard.writeText(deviceUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-10 pb-24">
      <div>
        <h1 className="text-xl font-bold text-text-primary">Wall Display</h1>
        <p className="text-sm text-text-secondary mt-1">
          Configure your household's always-on tablet display.
        </p>
      </div>

      {/* ── Section 1: Devices ── */}
      <section>
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Registered Devices</h2>

        {devices.length === 0 && (
          <p className="text-sm text-text-muted mb-3">No devices registered yet.</p>
        )}

        <div className="space-y-2 mb-4">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center justify-between bg-surface border border-border rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">{d.device_name}</p>
                {d.short_code && (
                  <p className="text-xs font-mono text-primary mt-0.5">doorchart.app/d/{d.short_code}</p>
                )}
                <p className="text-xs text-text-muted mt-0.5">
                  {d.last_seen_at
                    ? `Last seen ${new Date(d.last_seen_at).toLocaleString()}`
                    : 'Never connected'}
                </p>
              </div>
              <button
                onClick={() => removeDevice(d.id)}
                className="text-xs text-red-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {!showRegister && (
          <button
            onClick={() => { setShowRegister(true); setQrCode(null) }}
            className="text-sm font-semibold bg-primary text-white px-4 py-2.5 rounded-xl hover:bg-primary/90 transition-colors"
          >
            Register New Device
          </button>
        )}

        {showRegister && (
          <div className="bg-surface border border-border rounded-2xl p-5 mt-3">
            {!qrCode ? (
              <>
                <p className="text-sm font-semibold text-text-primary mb-3">Step 1: Name your device</p>
                <input
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  placeholder='e.g. "Kitchen Tablet"'
                  className="w-full border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 mb-3"
                />
                <div className="flex gap-2">
                  <button
                    onClick={registerDevice}
                    disabled={registering || !newDeviceName.trim()}
                    className="bg-primary text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {registering ? 'Generating…' : 'Generate Code'}
                  </button>
                  <button
                    onClick={() => { setShowRegister(false); setNewDeviceName('') }}
                    className="text-sm text-text-secondary px-4 py-2.5 rounded-xl hover:bg-background transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-sm font-semibold text-text-primary">Step 2: Open this address on your tablet</p>

                {/* Short URL — large, easy to read/type */}
                <div className="bg-background rounded-2xl px-5 py-4">
                  <p className="text-xs text-text-muted mb-1">Type this in the browser</p>
                  <p className="text-2xl font-bold font-mono tracking-widest text-primary">{shortUrl}</p>
                  <button
                    onClick={copyUrl}
                    className="mt-2 text-xs text-text-muted hover:text-primary transition-colors"
                  >
                    {copied ? '✓ Copied' : 'Copy link'}
                  </button>
                </div>

                {/* QR code */}
                <div>
                  <p className="text-xs text-text-muted mb-2">Or scan the QR code</p>
                  <div className="inline-block bg-white p-3 rounded-xl">
                    <QRCodeSVG value={deviceUrl} size={160} />
                  </div>
                </div>

                <p className="text-xs text-text-muted">
                  The display will activate automatically. Bookmark or install the page for fullscreen mode.
                </p>
                <button
                  onClick={() => { setShowRegister(false); setQrCode(null); setNewDeviceName('') }}
                  className="text-sm text-primary font-semibold"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Section 2: Idle Timeout ── */}
      <section>
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Display Preferences</h2>
        <div className="bg-surface border border-border rounded-2xl p-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Personal view idle timeout
          </label>
          <select
            value={settings?.idle_timeout_seconds ?? 120}
            onChange={(e) => setSettings((s) => ({ ...s, idle_timeout_seconds: Number(e.target.value) }))}
            className="w-full border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          >
            {TIMEOUT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </section>

      {/* ── Section 3: Panels ── */}
      <section>
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Panel Configuration</h2>
        <p className="text-xs text-text-muted mb-3">Drag to reorder. Toggle panels on/off. Set display duration.</p>
        <div className="space-y-2">
          {panels.map((panel, i) => (
            <div
              key={panel.id}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(i)}
              className="bg-surface border border-border rounded-xl px-4 py-3 flex items-center gap-3 cursor-grab active:cursor-grabbing"
            >
              <span className="text-text-muted text-sm cursor-grab">⠿</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-text-primary">{PANEL_LABELS[panel.id] ?? panel.id}</p>
                  {panel.admin_only && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md font-medium">Admin only</span>
                  )}
                </div>
                {panel.enabled && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-text-muted">Duration:</span>
                    <input
                      type="range"
                      min="10" max="120" step="5"
                      value={panel.duration_seconds}
                      onChange={(e) => updatePanel(panel.id, { duration_seconds: Number(e.target.value) })}
                      className="w-24"
                    />
                    <span className="text-xs text-text-muted w-10">{panel.duration_seconds}s</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => updatePanel(panel.id, { enabled: !panel.enabled })}
                className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${panel.enabled ? 'bg-primary' : 'bg-border'}`}
                role="switch" aria-checked={panel.enabled}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${panel.enabled ? 'left-[calc(100%-22px)]' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section 4: Weather ── */}
      <section>
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Weather</h2>
        <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text-primary">Show weather panel</p>
            <button
              onClick={() => setSettings((s) => ({ ...s, show_weather: !s.show_weather }))}
              className={`w-10 h-6 rounded-full transition-colors relative ${settings?.show_weather ? 'bg-primary' : 'bg-border'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${settings?.show_weather ? 'left-[calc(100%-22px)]' : 'left-0.5'}`} />
            </button>
          </div>

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1.5">Location</p>
            <div className="flex gap-2">
              <input
                value={locationSearch}
                onChange={(e) => { setLocationSearch(e.target.value); setLocationError('') }}
                onKeyDown={(e) => e.key === 'Enter' && searchLocation()}
                placeholder="Search city (e.g. Austin, Texas)"
                className="flex-1 border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                onClick={searchLocation}
                disabled={locationSearching || !locationSearch.trim()}
                className="text-xs font-semibold text-primary px-3 py-2 border border-primary/30 rounded-xl hover:bg-primary-light disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {locationSearching ? '…' : 'Search'}
              </button>
            </div>
            {locationError && <p className="text-xs text-rose-500 mt-1">{locationError}</p>}
            {settings?.weather_location_label && (
              <p className="text-xs text-primary font-medium mt-1.5">📍 {settings.weather_location_label}</p>
            )}
            {settings?.weather_latitude && !settings?.weather_location_label && (
              <p className="text-xs text-text-muted mt-1">
                {Number(settings.weather_latitude).toFixed(4)}, {Number(settings.weather_longitude).toFixed(4)}
              </p>
            )}
            <button
              onClick={detectLocation}
              className="mt-2 text-xs text-text-muted hover:text-primary transition-colors"
            >
              📍 Detect my location automatically
            </button>
          </div>

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1.5">Temperature unit</p>
            <div className="flex gap-2">
              {['fahrenheit', 'celsius'].map((u) => (
                <button
                  key={u}
                  onClick={() => setSettings((s) => ({ ...s, weather_unit: u }))}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${settings?.weather_unit === u ? 'bg-primary text-white' : 'border border-border text-text-secondary'}`}
                >
                  {u === 'fahrenheit' ? '°F' : '°C'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 5: Member settings ── */}
      <section>
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Member Display Settings</h2>
        <div className="space-y-3">
          {members.map((m) => {
            const p   = m.profiles
            const mds = memberDSettings[m.user_id] ?? {}
            return (
              <div key={m.user_id} className="bg-surface border border-border rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center text-xl">
                    {mds.avatar_emoji ?? p?.display_name?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{p?.display_name}</p>
                    <p className="text-xs text-text-muted capitalize">{m.role}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Show on display */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-text-secondary">Show on display</p>
                    <button
                      onClick={() => saveMemberSetting(m.user_id, { show_on_display: !(mds.show_on_display ?? true) })}
                      className={`w-9 h-5 rounded-full transition-colors relative ${(mds.show_on_display ?? true) ? 'bg-primary' : 'bg-border'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${(mds.show_on_display ?? true) ? 'left-[calc(100%-18px)]' : 'left-0.5'}`} />
                    </button>
                  </div>

                  {/* Avatar emoji */}
                  <div>
                    <p className="text-xs text-text-secondary mb-1.5">Display avatar emoji (optional)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {EMOJI_OPTIONS.map((e) => (
                        <button
                          key={e}
                          onClick={() => saveMemberSetting(m.user_id, { avatar_emoji: mds.avatar_emoji === e ? null : e })}
                          className={`w-8 h-8 text-xl rounded-lg transition-colors ${mds.avatar_emoji === e ? 'bg-primary-light ring-2 ring-primary' : 'hover:bg-background'}`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Kid mode (for limited members) */}
                  {m.role === 'limited' && (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-text-secondary">Kid mode</p>
                        <button
                          onClick={() => saveMemberSetting(m.user_id, { kid_mode_enabled: !mds.kid_mode_enabled })}
                          className={`w-9 h-5 rounded-full transition-colors relative ${mds.kid_mode_enabled ? 'bg-primary' : 'bg-border'}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${mds.kid_mode_enabled ? 'left-[calc(100%-18px)]' : 'left-0.5'}`} />
                        </button>
                      </div>
                      {mds.kid_mode_enabled && (
                        <div className="flex gap-2">
                          {['junior', 'teen'].map((t) => (
                            <button
                              key={t}
                              onClick={() => saveMemberSetting(m.user_id, { kid_mode_type: t })}
                              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors capitalize ${(mds.kid_mode_type ?? 'junior') === t ? 'bg-primary text-white' : 'border border-border text-text-secondary'}`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* PIN */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-text-secondary">Access PIN</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setPinModal({ userId: m.user_id, displayName: p?.display_name }); setPinInput(''); setPinError('') }}
                        className="text-xs text-primary font-semibold px-2 py-1 rounded-lg hover:bg-primary-light transition-colors"
                      >
                        Set PIN
                      </button>
                      <button
                        onClick={() => {
                          fetch(`${EDGE_BASE}/set-display-pin`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                            body: JSON.stringify({ target_user_id: m.user_id, household_id: householdId, pin: null }),
                          })
                        }}
                        className="text-xs text-text-muted px-2 py-1 rounded-lg hover:bg-background transition-colors"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Save button */}
      <div className="fixed bottom-0 inset-x-0 px-4 py-4 bg-background/95 border-t border-border backdrop-blur-sm">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="w-full max-w-2xl mx-auto block bg-primary text-white text-sm font-semibold py-3.5 rounded-2xl hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Display Settings'}
        </button>
      </div>

      {/* PIN Modal */}
      {pinModal && (
        <Portal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-text-primary mb-1">Set PIN for {pinModal.displayName}</h3>
            <p className="text-xs text-text-muted mb-4">Leave blank to remove PIN. Must be 4 digits.</p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError('') }}
              placeholder="4-digit PIN"
              className="w-full border border-border rounded-xl px-3 py-3 text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30 mb-3"
            />
            {pinError && <p className="text-red-500 text-xs mb-3">{pinError}</p>}
            <div className="flex gap-2">
              <button
                onClick={savePin}
                disabled={saving}
                className="flex-1 bg-primary text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? '…' : 'Save'}
              </button>
              <button
                onClick={() => setPinModal(null)}
                className="flex-1 border border-border text-text-secondary text-sm py-2.5 rounded-xl hover:bg-background"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  )
}
